import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CardContent, Checkbox, Chip, FormControlLabel, Grid, TextField } from '@mui/material';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { IconBuildingSkyscraper } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const BranchForm = () => {
    const history = useHistory();
    const location = useLocation();
    const { id } = useParams();
    const isEdit = Boolean(id);

    // Só é possível chegar aqui a partir da tela da empresa (que já manda
    // a empresa via `history.push(..., { company })`) - diferente do
    // fluxo antigo de Estabelecimentos, aqui não existe um buscador de
    // empresa: não faz sentido criar uma filial "solta" sem saber de qual
    // empresa ela é.
    const [company, setCompany] = useState(location.state?.company || null);

    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [loading, setLoading] = useState(isEdit);
    const [code, setCode] = useState(null);

    const [name, setName] = useState('');
    const [cnpj, setCnpj] = useState('');
    const [isMatriz, setIsMatriz] = useState(false);
    const [address, setAddress] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao buscar app padrão' });
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId || !isEdit) return;
        (async () => {
            try {
                const res = await api.get(`v1/apps/${appId}/branches/${id}/`);
                const data = res.data;
                setCode(data.code);
                setName(data.name);
                setCnpj(data.cnpj || '');
                setIsMatriz(data.is_matriz);
                setAddress(data.address || '');
                setPhone(data.phone || '');
                setEmail(data.email || '');
                setCompany({ id: data.company, name: data.company_name });
            } catch (error) {
                console.error(error);
                setStatus({ type: 'error', message: 'Erro ao carregar filial' });
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, isEdit, id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        if (!appId) {
            setStatus({ type: 'error', message: 'App não carregado ainda' });
            return;
        }
        if (!company) {
            setStatus({ type: 'error', message: 'Nenhuma empresa selecionada - volte e abra "Nova filial" a partir da tela da empresa' });
            return;
        }
        if (!name) {
            setStatus({ type: 'error', message: 'Informe o nome da filial' });
            return;
        }

        const payload = {
            company: company.id,
            name,
            cnpj: cnpj || null,
            is_matriz: isMatriz,
            address: address || null,
            phone: phone || null,
            email: email || null
        };

        try {
            if (isEdit) {
                await api.patch(`v1/apps/${appId}/branches/${id}/`, payload);
            } else {
                await api.post(`v1/apps/${appId}/branches/`, payload);
            }
            history.push(`/dashboard/config/companies/edit/${company.id}`);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao salvar filial' });
        }
    };

    if (loading) return null;

    return (
        <Box>
            <PageHeader
                icon={IconBuildingSkyscraper}
                gradient="teal"
                title={isEdit ? 'Editar Filial' : 'Nova Filial'}
                subtitle={company ? `Empresa: ${company.name}` : undefined}
                actions={code ? <Chip label={`#${code}`} /> : undefined}
            />

            <GlassCard>
                <CardContent>
                    {status.message && (
                        <Alert severity={status.type} sx={{ mb: 2 }}>
                            {status.message}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField label="Nome da filial" fullWidth required value={name} onChange={(e) => setName(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField label="CNPJ" fullWidth value={cnpj} onChange={(e) => setCnpj(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Telefone" fullWidth value={phone} onChange={(e) => setPhone(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Email" type="email" fullWidth value={email} onChange={(e) => setEmail(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControlLabel
                                    control={<Checkbox checked={isMatriz} onChange={(e) => setIsMatriz(e.target.checked)} />}
                                    label="É a matriz"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField label="Endereço" fullWidth value={address} onChange={(e) => setAddress(e.target.value)} />
                            </Grid>

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end" gap={2}>
                                    <Button
                                        onClick={() =>
                                            history.push(
                                                company
                                                    ? `/dashboard/config/companies/edit/${company.id}`
                                                    : '/dashboard/config/companies/view'
                                            )
                                        }
                                    >
                                        Voltar
                                    </Button>
                                    <Button type="submit" variant="contained">
                                        {isEdit ? 'Salvar Alterações' : 'Criar Filial'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default BranchForm;
